import { Stack, Tooltip, Typography } from '@mui/material';
import { Collection, GlobusError, TGlobusError, useGetCollections } from '../../hooks/useGlobusTransfer';
import LassoGrid from "../../components/LassoGrid";
import { PlaylistAddCheck, PlaylistRemove } from "@mui/icons-material";
import { ColDef } from "@ag-grid-community/core";
import React, { useState } from "react";
import GlobusErrorView from './GlobusErrorView';
import { useDispatch } from "react-redux";
import {
    setUserCollection,
} from "../data-wrangler/globusSlice";
import { LassoTab, LassoTabs } from "../../components/LassoTabs";
import SearchBar from "../../components/form/SearchBar";

const GCPAUSED = "ClientError.Conflict.GCPaused"

enum CollectionFilters {
    MyEndpoints = 'my-endpoints',
    AdministeredByMe = 'administered-by-me',
    Search = 'all',
}

const CollectionCell = (props: any) => {

    return (
        <Stack height={"100%"} flexDirection={"row"} alignItems={"center"} gap={3}>
            {props.data.active
                ? <PlaylistAddCheck color={'success'} fontSize="large" />
                : (
                    <Tooltip title={'Globus unable to connect to collection'}>
                        <PlaylistRemove color={'error'} fontSize="large" />
                    </Tooltip>
                )
            }
            <Typography variant={"body1"}>{props.data.label}</Typography>
        </Stack>
    )
}

const GlobusCollectionColumnDef: ColDef[] = [
    {
        field: "label",
        flex: 1,
        cellRenderer: CollectionCell
    }
];

const GlobusCollections = ({ reload }: any) => {
    const dispatch = useDispatch();
    const [showError, setShowError] = useState(false);
    const [currentSection, setCurrentSection] = useState(CollectionFilters.MyEndpoints);
    const [collectionQuery, setCollectionQuery] = useState<any>({
        queryKey: CollectionFilters.MyEndpoints,
        queryParams: {
            query: {
                filter_scope: currentSection
            }
        }
    });
    const { data: collections, isSuccess, isFetching } = useGetCollections(collectionQuery.queryKey, collectionQuery.queryParams);

    const userWithoutCollectionError = new GlobusError("NO_COLLECTIONS_AVAILABLE", '') as TGlobusError;

    const onRowDoubleClicked = (e: any) => {
        if (e.data.active) {
            dispatch(setUserCollection({
                id: e.data.value,
                label: e.data.label
            }));
        } else {
            setShowError(true)
        }
    }

    // This is in the expected format of the Globus SDK.
    // Ignoring camelcase rule.
    /* eslint-disable camelcase */
    const handleTabChange = (event: React.SyntheticEvent, newTab: CollectionFilters) => {
        setCurrentSection(newTab)
        if (newTab === CollectionFilters.Search) {
            setCollectionQuery({
                queryKey: "",
                queryParams: {
                    query: {
                        filter_fulltext: "",
                        limit: 100,
                        offset: 0,
                        filter_non_functional: 0,
                        filter_scope: "hide-no-permissions"
                    }
                }
            })
        } else {
            setCollectionQuery({
                queryKey: newTab,
                queryParams: {
                    query: {
                        filter_scope: newTab
                    }
                }
            })
        }
    }

    const setSearchValue = (value: string) => {
        setCollectionQuery({
            queryKey: value,
            queryParams: {
                query: {
                    filter_fulltext: value,
                    limit: 100,
                    offset: 0,
                    filter_non_functional: 0,
                    filter_scope: "hide-no-permissions"
                }
            }
        })
    }

    /* eslint-enable camelcase */
    const renederCollectionList = (collections: any[], action: Function) => (
        <>
            <Typography variant={'h5'}>
                Select a Collection to Proceed
            </Typography>
            <Typography variant={'h6'}>
                Double click on a collection below to continue with your file transfer request.
            </Typography>
            {currentSection === CollectionFilters.Search && (
                <SearchBar onChange={(value: string) => setSearchValue(value)} />
            )}
            <LassoGrid
                idKey={'globus_collection_grid'}
                columns={GlobusCollectionColumnDef}
                data={collections}
                searchable={currentSection !== CollectionFilters.Search}
                tab={""}
                showHide={[]}
                onRowDoubleClicked={action}
            />
        </>
    )

    if (showError) {
        const selectCollectionError = new GlobusError(GCPAUSED, '') as TGlobusError;
        return (
            <GlobusErrorView
                isAuthenticated={true}
                isSuccess={isSuccess}
                globusError={selectCollectionError}
                reload={() => setShowError(false)}
                login={() => null}
                reauth={() => null}
            />
        )
    }

    const render = () => {
        let mappedCollections: any[] = [];
        if(isSuccess && collections) {
            mappedCollections = collections["DATA"]
                .map((collection: Collection) => {
                    if (collection.entity_type == 'GCP_mapped_collection' && !collection.gcp_connected) return null;
                    return {
                        label: collection.display_name,
                        value: collection.id,
                        active: collection.entity_type == 'GCP_mapped_collection' ? collection.gcp_connected : true
                    }
                }).filter((collection: any) => !!collection)
        }
        if (mappedCollections.length > 0 || isFetching) {
            return renederCollectionList(mappedCollections, onRowDoubleClicked)
        }
        return <GlobusErrorView
            isAuthenticated={true}
            isSuccess={isSuccess}
            globusError={userWithoutCollectionError}
            reload={reload}
            login={() => null}
            reauth={() => null}
        />
    }

    return (
        <Stack flexDirection="column" gap={3} height="100%">
            <LassoTabs value={currentSection}  onChange={handleTabChange}>
                <LassoTab label="My Endpoints" value={CollectionFilters.MyEndpoints}/>
                <LassoTab label="Administered By You" value={CollectionFilters.AdministeredByMe}/>
                <LassoTab label="Search" value={CollectionFilters.Search}/>
            </LassoTabs>
            { render() }
        </Stack>
    )
}

export default GlobusCollections;